/**
 * FilePreviewContent Component
 * 
 * Reusable file preview content for use within Modal component.
 * Handles file fetching, blob URL creation, and rendering preview for different file types.
 * Supports images, PDFs, Office docs, and provides download for other types.
 */

import React, { useState, useEffect, useCallback } from 'react';

type FilePreviewContentProps = {
  file: {
    id: number;
    file_name: string;
    mime_type: string;
    download_url: string;
  } | null;
  downloadFile: (fileId: number) => Promise<Blob>;
  isOpen: boolean;
};

const FilePreviewContent = ({ 
  file, 
  downloadFile,
  isOpen 
}: FilePreviewContentProps) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [retryTrigger, setRetryTrigger] = useState(0);

  useEffect(() => {
    if (!file || !isOpen) {
      setBlobUrl(null);
      setLoading(false);
      setError(null);
      return;
    }

    const isPreviewable = file.mime_type.startsWith('image/') || file.mime_type === 'application/pdf';

    setBlobUrl(null);
    setError(null);
    if (!isPreviewable) {
      setLoading(false);
      return;
    }
    setLoading(true);

    let createdUrl: string | null = null;
    let cancelled = false;

    void (async () => {
      try {
        const blob = await downloadFile(file.id);
        const url = URL.createObjectURL(blob);
        createdUrl = url;
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        setBlobUrl(url);
        setLoading(false);
      } catch (err) {
        if (cancelled) {
          return;
        }
        console.error('Failed to load file:', err);
        setError(err instanceof Error ? err.message : 'Failed to load file');
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (createdUrl) {
        URL.revokeObjectURL(createdUrl);
      }
    };
  }, [file, isOpen, downloadFile, retryTrigger]);

  const handleDownload = useCallback(async () => {
    if (!file) {
      return;
    }

    setDownloading(true);
    try {
      const blob = await downloadFile(file.id);
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = file.file_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
    } catch (downloadError) {
      console.error('Failed to download file:', downloadError);
      setError(downloadError instanceof Error ? downloadError.message : 'Failed to download file');
    } finally {
      setDownloading(false);
    }
  }, [downloadFile, file]);

  if (!file) return null;

  // Determine file type
  const isImage = file.mime_type.startsWith('image/');
  const isPDF = file.mime_type === 'application/pdf';
  const isOfficeDoc = [
    'application/vnd.openxmlformats-officedocument',
    'application/msword',
    'application/vnd.ms-excel'
  ].some(type => file.mime_type.includes(type));

  return (
    <div className="file-preview-container">
      {/* File metadata */}
      <div className="file-preview-meta">
        <button
          type="button"
          onClick={() => {
            void handleDownload();
          }}
          className="btn-secondary file-download-btn"
          disabled={downloading}
        >
          {downloading ? 'Téléchargement...' : '⬇ Télécharger'}
        </button>
      </div>

      {/* Preview content */}
      <div className="file-preview-content">
        {loading && (
          <div className="preview-loading">
            <div className="preview-placeholder">
              <p>Chargement du fichier...</p>
            </div>
          </div>
        )}

        {error && (
          <div className="preview-error">
            <div className="preview-placeholder">
              <p style={{ color: '#dc3545' }}>Erreur: {error}</p>
              <button
                type="button"
                onClick={() => {
                  setError(null);
                  setRetryTrigger((c) => c + 1);
                }}
                className="btn-primary"
                disabled={loading}
              >
                Réessayer
              </button>
              <button
                type="button"
                onClick={() => {
                  void handleDownload();
                }}
                className="btn-secondary"
                disabled={downloading}
                style={{ marginLeft: 8 }}
              >
                {downloading ? 'Téléchargement...' : 'Télécharger le fichier'}
              </button>
            </div>
          </div>
        )}

        {!loading && !error && blobUrl && isImage && (
          <div className="image-preview">
            <img 
              src={blobUrl} 
              alt={file.file_name}
              style={{ maxWidth: '100%', height: 'auto', display: 'block', margin: '0 auto' }}
            />
          </div>
        )}
        
        {!loading && !error && blobUrl && isPDF && (
          <div className="pdf-preview">
            <iframe 
              src={blobUrl}
              style={{ width: '100%', height: '75vh', border: 'none' }}
              title={file.file_name}
            />
          </div>
        )}
        
        {!loading && !error && !isImage && !isPDF && (
          <div className="preview-not-available">
            <div className="preview-placeholder">
              <svg 
                width="64" 
                height="64" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path>
                <polyline points="13 2 13 9 20 9"></polyline>
              </svg>
              <p>
                {isOfficeDoc
                  ? 'Aperçu non disponible pour les documents Office'
                  : 'Aperçu non disponible pour ce type de fichier'}
              </p>
              <button
                type="button"
                onClick={() => {
                  void handleDownload();
                }}
                className="btn-primary"
                disabled={downloading}
              >
                {downloading ? 'Téléchargement...' : 'Télécharger le fichier'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FilePreviewContent;

